import { useState } from "react";
import { ArrowDownLeft, ArrowUpRight, Ban, CreditCard, Percent, RotateCcw, ShoppingBag } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cardMoney, operationAmount, operationDate } from "@/lib/cardMoney";
import { cn } from "@/lib/utils";
import MerchantIcon, { matchBrand } from "@/components/cards/MerchantIcon";
import OperationSheet from "@/components/cards/OperationSheet";
import { categoryIconFor } from "@/components/cards/CategoryIcon";
import { mccCategory } from "@/components/cards/mccCategories";

// Лента операций по карте.
//
// Главное правило здесь — не прятать комиссии. Провайдер отдаёт их отдельными
// строками с типом (cross_border, maintenance, decline_*), и растворять их в
// сумме покупки нельзя: именно из этих строк человек узнаёт, что покупка на
// $73.41 в батах стоила ему $75.55.
//
// Второе правило — не считать баланс по этой ленте. Не каждая строка двигает
// баланс, часть движений сюда не попадает вовсе, и источник истины ровно один:
// GET /cards/{id}/balance.

const money = (minor, scale = 2) =>
  minor === null || minor === undefined ? "—" : `$${(minor / 10 ** scale).toFixed(2)}`;

const ICONS = {
  issue: CreditCard,
  settlement: ShoppingBag,
  authorization: ShoppingBag,
  fee: Percent,
  refund: RotateCcw,
  reversal: RotateCcw,
  fund: ArrowDownLeft,
  unload: ArrowUpRight,
};

const CardHistory = ({ items, limit, onShowAll, showAll, last4 }) => {
  const { t } = useTranslation();
  // Подробности операции живут здесь же: строке ленты негде показать
  // категорию, код отказа и идентификатор, а именно за ними человек и тычет.
  const [selected, setSelected] = useState(null);

  if (!items) return null;
  if (items.length === 0) {
    return (
      <div className="lp-glass px-4 py-6 text-center">
        <p className="text-xs text-muted-foreground">{t("cards.history_empty")}</p>
      </div>
    );
  }

  const shown = showAll || !limit ? items : items.slice(0, limit);

  const titleOf = (op) => {
    if (op.displayTitle) return op.displayTitle;
    if (op.source === "ours") {
      return op.type === "fund" ? t("cards.op_fund") : t("cards.op_unload");
    }
    if (["refund", "reversal"].includes(op.type)) return t("cards.op_refund");
    if (op.type === "fee") {
      // Тип комиссии — закрытый словарь провайдера, поэтому переводим его, а на
      // незнакомом значении честно говорим «комиссия», а не показываем код.
      const known = ["cross_border", "maintenance", "decline_domestic", "decline_international"];
      return known.includes(op.feeType) ? t(`cards.fee_${op.feeType}`) : t("cards.op_fee");
    }
    if (op.status === "declined") return op.merchantName || t("cards.op_decline");
    return op.merchantName || t("cards.op_purchase");
  };

  const subtitleOf = (op) => {
    if (op.displaySubtitle) return op.displaySubtitle;
    const parts = [];
    if (op.source === "ours") {
      parts.push(op.type === "fund" ? t("cards.op_fund_from") : t("cards.op_unload_to"));
      if (op.status === "processing") parts.push(t("cards.op_processing"));
      if (op.status === "refunded") parts.push(t("cards.op_failed"));
    } else {
      // Провайдер присылает код и почти всегда пустое описание — проверено на
      // живой ленте. «MCC 5499» человеку не говорит ничего, поэтому разбираем
      // код в название категории сами. Сырой код оставляем на самый крайний
      // случай: соврать «Покупки» про неизвестную категорию хуже, чем
      // показать код и дать понять, что мы её не опознали.
      const category = mccCategory(op.mcc);
      if (op.mccDescription) parts.push(op.mccDescription);
      else if (category) parts.push(t(`cards.mcc_${category}`));
      else if (op.mcc) parts.push(`MCC ${op.mcc}`);
      if (op.declineCode) parts.push(t("cards.op_decline"));
    }
    if (op.source !== "ours" && op.originalAmount) parts.push(cardMoney(op.originalAmount.amountMinor, op.originalAmount.scale ?? 2, op.originalAmount.currency));
    if (op.source !== "ours" && op.type !== "fee" && op.feeMinor > 0) parts.push(`${t("cards.issuer_fee")}: ${cardMoney(op.feeMinor, op.scale ?? 2, op.currency)}`);
    if (op.occurredAt) parts.push(operationDate(op.occurredAt));
    return parts.join(" · ");
  };

  return (
    <div className="lp-glass overflow-hidden">
      <div className="flex items-center justify-between border-b border-[var(--lp-hair)] px-3.5 py-3">
        <span className="text-sm font-semibold text-foreground">{t("cards.history_title")}</span>
        {limit && items.length > limit && (
          <button
            type="button"
            onClick={onShowAll}
            className="text-xs text-muted-foreground"
            data-testid="history-toggle"
          >
            {showAll ? t("cards.history_less") : t("cards.history_all")}
          </button>
        )}
      </div>

      <div className="divide-y divide-[var(--lp-hair)]">
        {shown.map((op) => {
          const Icon = ICONS[op.type] || ShoppingBag;
          const declined = op.status === "declined";
          const incoming = op.direction === "in";
          // Логотип ставим только настоящей покупке у названного мерчанта.
          // У комиссии, пополнения и отказа важен ТИП операции, а не бренд:
          // подменять смысловой значок логотипом там — терять смысл.
          const merchant =
            op.source !== "ours" && op.type !== "fee" && !declined ? op.merchantName : null;
          const branded = Boolean(merchant) && Boolean(matchBrand(merchant));
          // Порядок значков: логотип бренда, потом категория по MCC, потом
          // монограмма. Для «Amora Beach Resort Phuket» значок отеля полезнее
          // букв «AB», а для «cplusplus.com-2C2P (THAI» — тем более.
          const CategoryGlyph = merchant && !branded ? categoryIconFor(op.mcc) : null;
          return (
            <button
              key={`${op.source}-${op.id}`}
              type="button"
              onClick={() => setSelected(op)}
              className="lp-press flex w-full items-start gap-3 px-3.5 py-3 text-left"
              data-testid={`operation-${op.source}-${op.id}`}
            >
              <span
                className={cn(
                  "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-[10px]",
                  branded && "bg-transparent",
                  CategoryGlyph && "lp-tile text-muted-foreground",
                  !merchant && op.type === "fee" && "bg-amber-500/15 text-amber-500",
                  !merchant && declined && "bg-destructive/15 text-destructive-fg",
                  !merchant && !declined && op.type !== "fee" && incoming && "bg-emerald-500/15 text-emerald-500",
                  !merchant && !declined && op.type !== "fee" && !incoming && "bg-[var(--lp-tile)] text-muted-foreground"
                )}
              >
                {branded ? (
                  <MerchantIcon name={merchant} />
                ) : CategoryGlyph ? (
                  <CategoryGlyph className="h-4 w-4" />
                ) : merchant ? (
                  <MerchantIcon name={merchant} className="rounded-[10px]" />
                ) : declined ? (
                  <Ban className="h-4 w-4" />
                ) : (
                  <Icon className="h-4 w-4" />
                )}
              </span>

              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-foreground">
                  {titleOf(op)}
                  {op.status === "pending" && (
                    <span className="ml-2 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-500">
                      {t("cards.op_hold")}
                    </span>
                  )}
                </span>
                <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
                  {subtitleOf(op)}
                </span>
              </span>

              <span
                className={cn(
                  "shrink-0 text-sm font-semibold tabular-nums",
                  incoming ? "text-emerald-500" : "text-foreground"
                )}
              >
                {declined ? "" : incoming ? "+" : "−"}
                {cardMoney(operationAmount(op), op.scale ?? 2, op.currency || "USD")}
              </span>
            </button>
          );
        })}
      </div>

      <OperationSheet op={selected} last4={last4} onClose={() => setSelected(null)} />
    </div>
  );
};

export default CardHistory;
